import {
  Button,
  Divider,
  Flex,
  Heading,
  Icon,
  IconButton,
  Input,
  Text,
} from '@chakra-ui/react';
import { ArrowLeftIcon, CheckCheckIcon, PencilIcon } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IndigoLogoGradient } from '~/common/icons/IndigoLogoGradient';
import { useBuyerOfferAnalysisContext } from './Context/BuyerOfferAnalysisContext';
import { OfferScoreGauge } from './OfferScore/OfferScoreGauge';
import { PreparedBy } from './PreparedBy';

interface BuyerOfferAnalysisHeaderProps {
  showOfferScore?: boolean;
  onOfferCoachClick: () => void;
}

export const BuyerOfferAnalysisHeader = ({
  showOfferScore,
  onOfferCoachClick,
}: BuyerOfferAnalysisHeaderProps) => {
  const {
    buyerOfferAnalysis,
    title,
    updateTitle,
    setTitle,
    isViewOnly,
    isChangesSaved,
    onShareWithBuyerModalOpen,
    onEditPartyModalOpen,
    isMobile,
    headerHeight,
    setShouldOpenShareModal,
  } = useBuyerOfferAnalysisContext();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const navigate = useNavigate();

  const onBlur = () => {
    updateTitle(title);
    setIsEditingTitle(false);
  };

  const onShare = () => {
    if (!buyerOfferAnalysis?.party) {
      setShouldOpenShareModal(true);
      onEditPartyModalOpen();
    } else {
      onShareWithBuyerModalOpen();
    }
  };

  const header = (
    <Flex
      alignItems="center"
      backdropFilter="blur(4px)"
      bg="indigo.700"
      borderBottom="1px solid"
      borderBottomColor="whiteAlpha.200"
      direction={isMobile ? 'column' : 'row'}
      justifyContent="space-between"
      position="fixed"
      pr={isMobile ? 0 : 4}
      top={0}
      width="100%"
      zIndex={1}
    >
      <Flex
        alignItems="center"
        direction={isMobile && isViewOnly ? 'column' : 'row'}
        height={headerHeight}
        width="100%"
      >
        <Flex alignItems="center" height="100%" minWidth="0" width="100%">
          {!isViewOnly && (
            <>
              <Flex alignItems="center" height="100%" px={{ base: 2, md: 5 }}>
                <IconButton
                  aria-label="Back"
                  as={Link}
                  color="white"
                  icon={<ArrowLeftIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(-1)}
                />
              </Flex>
              {!isMobile && <Divider height="100%" orientation="vertical" />}
            </>
          )}
          <Flex
            alignItems="center"
            gap={{ base: 0, md: 2 }}
            height="100%"
            justifyContent={isViewOnly ? 'center' : 'flex-start'}
            minWidth="0"
            mr={{
              base: isViewOnly ? 0 : 2,
              md: !isViewOnly || showOfferScore ? 0 : '70px',
            }}
            width="100%"
          >
            {isEditingTitle && !isViewOnly ? (
              <Input
                autoFocus
                value={title}
                width={{ base: '100%', md: '300px' }}
                onBlur={onBlur}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onBlur();
                  }
                }}
              />
            ) : (
              <>
                <Heading
                  color="white"
                  fontWeight="medium"
                  overflow="hidden"
                  pl={{ base: 0, md: 4 }}
                  size="headline"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {title}
                </Heading>
                {!isViewOnly && (
                  <IconButton
                    aria-label="Edit Offer Scorecard Title"
                    boxSize={8}
                    color="whiteAlpha.600"
                    icon={<PencilIcon size={16} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEditingTitle(true)}
                  />
                )}
              </>
            )}
          </Flex>
        </Flex>

        {isViewOnly && (
          <Flex alignItems="center" gap={4} height="100%">
            {showOfferScore && <OfferScoreGauge size="sm" />}

            <PreparedBy
              borderLeft={isMobile ? 'none' : '1px solid'}
              borderLeftColor="whiteAlpha.200"
            />
          </Flex>
        )}
        {!isViewOnly && (
          <Flex alignItems="center" gap={4} pr={4}>
            {!isMobile && (
              <Flex alignItems="center" gap={2} whiteSpace="nowrap">
                {isChangesSaved && (
                  <Icon as={CheckCheckIcon} color="green.500" size={16} />
                )}
                <Text color="whiteAlpha.600">
                  {isChangesSaved ? 'All changes saved' : 'Changes not saved'}
                </Text>
              </Flex>
            )}
            <Button
              borderRadius={{ base: 'full', md: 'xs' }}
              fontWeight="medium"
              size="sm"
              variant="gradient"
              onClick={onShare}
            >
              {isMobile ? 'Share' : 'Share with buyer'}
            </Button>
            <Button
              borderColor="whiteAlpha.300"
              borderRadius="full"
              color="white"
              fontWeight="medium"
              leftIcon={<IndigoLogoGradient />}
              size="sm"
              variant="outline"
              onClick={onOfferCoachClick}
            >
              Offer Coach
            </Button>
            {showOfferScore && <OfferScoreGauge size="sm" />}
          </Flex>
        )}
      </Flex>
    </Flex>
  );

  return header;
};
